<template>
  <vz-card
    class="vz-multi-order-request-card"
    :class="{'vz-multi-order-request-card-error': order.meta.error}"
    :variant="cardVariant"
  >
    <vz-loader :status-loader="order.meta.loading || loading" />
    <div class="vz-multi-order-request-card-title">
      <div class="vz-multi-order-request-card-title-label">
        <div class="text-overflow" :title="label">{{ label }}</div>
        <vz-tooltip v-if="order.meta.error" flex class="ml-5">
          <vz-icon  name="alert-circle" variant="primary" />
          <template #balloon>
            <span v-html="order.meta.error" />
          </template>
        </vz-tooltip>
      </div>
      <template v-if="!isCreated">
        <vz-icon
          name="delete"
          size="16"
          title="Удалить"
          class="vz-cursor-pointer"
          @click="orderStore.multiOrder.deleteRequest(index)"
        />
        <vz-icon
          name="content-copy"
          size="16"
          title="Создать на основе"
          class="vz-cursor-pointer"
          @click="clone"
        />
        <vz-icon
          name="open-in-new"
          size="16"
          title="Открыть"
          class="vz-cursor-pointer"
          @click="openRequest"
        />
      </template>
      <nuxt-link
        v-else
        :to="{name: 'personal-order-guid', params: { guid: order.meta.id }}"
        class="vz-multi-order-request-card-button"
      >
        <vz-icon
          name="open-in-new"
          size="16"
          title="Открыть"
          class="vz-cursor-pointer"
        />
      </nuxt-link>
    </div>
    <div class="vz-multi-order-request-card-body">
      <div>
        <b>Куда</b>
        <div class="mt-5 vz-multi-order-request-card-body-item">
          <vz-icon v-bind="destinationIconOptions" size="16" />
          <span style="margin-top: 1px">{{ destinationText }}</span>
        </div>
      </div>
      <div>
        <b>{{ date }}</b>
        <div class="color-light mt-5">{{ time }}</div>
      </div>
      <div>
        <b>Получатель</b>
        <div class="mt-5 vz-multi-order-request-card-body-item">
          <vz-icon v-bind="customerIconOptions" size="16" />
          <span style="margin-top: 1px">{{ customerText }}</span>
        </div>
      </div>
      <div>
        <b>Сумма заказа</b>
        <div class="mt-5">
          <img :src="countryFlag">
          <span class="balance ml-5">
            {{ priceText }}
            </span>
        </div>
      </div>
    </div>
  </vz-card>
</template>

<script setup lang="ts">
import {useOrderStore} from '~/stores/order';
import {PropType} from 'vue';
import VzCard from '~/components/ui-kit/vz-card.vue';
import {OrderForm} from '~/types/types';
import {asyncComputed} from '@vueuse/core';
import {useCityStore} from '~/stores/city';
import VzIcon from '~/components/ui-kit/vz-icon.vue';
import {formatDate} from '~/common/date';
import {priceFormat} from '~/common/price';
import {getCountryFlagIcon, getCurrencyText, priceDataToOrderForm} from '~/common/objects';
import VzTooltip from '~/components/ui-kit/vz-tooltip.vue';
import VzLoader from '~/components/ui-kit/vz-loader.vue';
import {getOrder} from '~/api/order';

const props = defineProps({
  order: {
    type: Object as PropType<OrderForm>,
    default: () => ({})
  },
  index: {
    type: Number,
    default: 1
  },
  guid: {
    type: String,
    default: ''
  }
})

const orderStore = useOrderStore()
const cityStore = useCityStore()

const loading = ref(false)
const orderData = ref<OrderForm>()

const isCreated = computed(() => {
  return !!props.guid && !loading.value
})

const orderSource = computed(() => {
  return isCreated.value ? orderData.value : props.order
})

const label = computed(() => {
  let number = `Заявка ${props.index}`
  if (isCreated.value) {
    number = `Заказ ${orderData.value?.meta?.number || ''}`
  }

  if (orderSource.value?.cargo.transportationId) {
    number += ` | ${orderSource.value?.cargo.transportationId}`
  }
  return `${number}${isCreated.value ? ' оформлен' : ''}`
})

const cardVariant = computed(() => {
  if (loading.value) {
    return 'inactive'
  }
  if (isCreated.value) {
    return 'info'
  }
  return ''
})

const destinationIconOptions = computed(() => {
  switch (orderSource.value?.directions.destination.direction) {
    case 'address':
      return { url: '/svg/icon/vz-home.svg', noMaskedIcon: true, variant: 'primary'}
    case 'terminal':
      return { url: '/svg/vz-terminal-red.svg', noMaskedIcon: true }
    case 'pickupPoint':
      return { url: '/svg/vz-hand-cargo.svg', noMaskedIcon: true, variant: 'primary' }
  }
})

const destinationText = asyncComputed(async () => {
  return (await cityStore.locationByGuid(orderSource.value?.directions.destination.locationGuid))?.name
}, '')

const date = computed(() => {
  return formatDate(orderSource.value?.directions.destination.calendar.date)
})

const time = computed(() => {
  const data = {
    type: orderSource.value?.directions.destination.direction,
    from: orderSource.value?.directions.destination.calendar.from,
    to: orderSource.value?.directions.destination.calendar.to,
    terminalTime: orderSource.value?.directions.destination.calendar.terminalTime,
  }

  if (data.type === 'address') {
    return [data.from, data.to].join('-')
  }
  return data.terminalTime
})

const customerIconOptions = computed(() => {
  switch (orderSource.value?.customer.destination.type) {
    case 'individual':
      return { name: 'account', variant: 'primary' }
    case 'corporation':
      return { name: 'domain', variant: 'primary' }
  }
})

const customerText = computed(() => {
  switch (orderSource.value?.customer.destination.type) {
    case 'individual':
      return orderSource.value?.customer.destination.individual.name.value
    case 'corporation':
      return orderSource.value?.customer.destination.corporation.companyName.value
  }
})

const currency = computed(() => {
  return Object.keys(orderSource.value?.totalPrice?.currency || {}).find(c => orderSource.value?.totalPrice.currency[c] === true) || 'RU'
})

const countryFlag = computed(() => {
  return getCountryFlagIcon(currency.value)
})

const priceText = computed(() => {
  return priceFormat(orderSource.value?.totalPrice?.total) + ' ' + getCurrencyText(currency.value, currency.value === 'RU')
})

function openRequest() {
  orderStore.multiOrder.setData(props.order)
  orderStore.multiOrder.selectedRequestId = props.index
}

function clone() {
  orderStore.multiOrder.setData(props.order)
  orderStore.multiOrder.addRequest(props.order)
}

watch(() => props.guid, async () => {
  if (props.guid) {
    loading.value = true
    const data = await getOrder({ id: props.guid })
      .finally(() => {
        loading.value = false
      })
    orderData.value = priceDataToOrderForm(data)
  }
}, { immediate: true })
</script>

<style lang="scss" scoped>
.vz-multi-order-request-card {
  overflow: hidden;

  &-title {
    padding: 14px 15px;
    background-color: $component-background;
    display: flex;
    align-items: center;
    gap: 20px;
    border-top-right-radius: 6px;
    border-top-left-radius: 6px;

    &-label {
      display: flex;
      align-items: center;
      margin-right: auto;
      overflow: hidden;
    }
  }

  &-error {
    border-color: $error;
    .vz-multi-order-request-card-title {
      background-color: var(--error-light);
    }
  }

  &-body {
    padding: 16px 15px 25px 15px;
    display: grid;
    grid-template-columns: 2fr 1fr;
    row-gap: 20px;
    column-gap: 10px;
    line-height: 16px;

    &-item {
      display: flex;
      align-items: flex-start;
      gap: 5px;
    }

    b {
      font-weight: 500;
    }

    @media screen and (max-width: $pub-md) and (min-width: $pa-sm + 1) {
      grid-template-columns: 1fr;
    }

    @media screen and (max-width: $pub-xs) {
      grid-template-columns: 1fr;
    }
  }

  &-button {
    color: inherit;
  }
}
</style>
